import { Trash } from "@medusajs/icons"
import { Button, Container, Heading, Text, Textarea, toast, usePrompt } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"
import { dateTime } from "../lib/format"
import { t } from "../lib/i18n"
import { errorMessage, hqApi } from "../lib/medusa"

/**
 * Notes on an order, for the owners only (never sent to the customer, the sheet or Medusa's order):
 * "called her, wants M instead", "pays the rest by IBAN on Friday". Site orders are keyed
 * `site:<order id>`, custom orders `custom:<number>`.
 */
export function OrderNotes({ orderKey }: { orderKey: string }) {
  const queryClient = useQueryClient()
  const prompt = usePrompt()
  const [draft, setDraft] = useState("")
  const queryKey = ["hq", "notes", orderKey]
  const notes = useQuery({ queryKey, queryFn: () => hqApi.notes(orderKey).then((body) => body.notes) })
  const add = useMutation({
    mutationFn: (body: string) => hqApi.addNote(orderKey, body),
    onSuccess: () => {
      setDraft("")
      void queryClient.invalidateQueries({ queryKey })
    },
    onError: (error) => toast.error(errorMessage(error)),
  })
  const remove = useMutation({
    mutationFn: (id: string) => hqApi.deleteNote(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
    onError: (error) => toast.error(errorMessage(error)),
  })

  const askDelete = async (id: string) => {
    const ok = await prompt({ title: t("Delete this note?"), description: t("It goes for everyone using HQ."), confirmText: t("Delete"), cancelText: t("Keep it") })
    if (ok) remove.mutate(id)
  }

  const list = notes.data ?? []
  return (
    <Container className="flex flex-col gap-y-3">
      <div className="flex items-baseline justify-between gap-x-2">
        <Heading level="h2">{t("Notes")}</Heading>
        <Text size="xsmall" className="text-ui-fg-muted">{t("For the team · never sent to the customer")}</Text>
      </div>
      {notes.isError && <Text size="small" className="text-ui-fg-error">{errorMessage(notes.error)}</Text>}
      {list.length > 0 && (
        <ul className="flex flex-col gap-y-2">
          {list.map((note) => (
            <li key={note.id} className="bg-ui-bg-subtle flex items-start gap-x-2 rounded-md px-3 py-2">
              <div className="min-w-0 flex-1">
                <Text size="small" className="whitespace-pre-wrap break-words">{note.body}</Text>
                <Text size="xsmall" className="text-ui-fg-muted">{[note.author, dateTime(note.created_at)].filter(Boolean).join(" · ")}</Text>
              </div>
              <button type="button" aria-label={t("Delete this note")} className="text-ui-fg-muted hover:text-ui-fg-base p-1" onClick={() => void askDelete(note.id)}>
                <Trash />
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex flex-col gap-y-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (draft.trim()) add.mutate(draft.trim())
        }}
      >
        <Textarea
          aria-label={t("New note")}
          rows={2}
          maxLength={2000}
          placeholder={list.length ? t("Add a note") : t("e.g. Wants size M instead, pays the rest on Friday")}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        <Button type="submit" size="small" variant="secondary" className="w-fit" isLoading={add.isPending} disabled={!draft.trim()}>
          {t("Save note")}
        </Button>
      </form>
    </Container>
  )
}
